import { SHAPE_TEMPLATES } from '../model/shape';
import { bandConfigFor } from '../config/generation-config';
import { targetScoreForLevel } from '../generator/difficulty-curve';
import { dependencyCount, layoutSignature } from '../generator/free-generator';
import { proxyScoreBoard } from '../generator/difficulty-score';
import { clearableFree } from '../solver/free-solver';
import { shortSolve } from '../solver/short-solve';
/**
 * Module 63 (WP-63.E1) — the scripted-level seam.
 *
 * Deliberately narrow: ONE map from level number to a hand-authored board, ONE
 * lookup in the plugin before the generator runs, and nothing that could grow
 * into a level editor. Decision 13: the generator aims for a difficulty score,
 * not for clarity, so the level that TEACHES powered gates is the one level
 * where a scripted board earns its special case.
 *
 * A scripted level is still a `FreeLevel`: its metrics are MEASURED here with
 * the same solver and score the generator uses, never typed in by hand, so the
 * move budget, the reward tier and the Studio's readouts see a board like any
 * other. `usedFallback: false` and a real `proxyPar` are what keep the budget
 * on the par-derived formula rather than the "unverified board" floor.
 *
 * The Studio's Batch and Play tabs, and the drift harness, read the GENERATOR
 * directly and therefore show the generated L25, not this one. Recorded in the
 * plan rather than fixed: widening the seam into every generator caller is the
 * level-editor ambition this WP refuses.
 */
/**
 * The map, DERIVED from the config rather than written as a literal.
 *
 * Module 65 (WP-65.A2) changed it from `{ 25: … }`. The board's whole job is to
 * be the first powered board a player meets, so its level is
 * `unlocks.poweredGatesUnlockLevel` by definition — and when that value moved
 * (25 -> 22, so a Beginner-track player meets the mechanic at all), a literal
 * key would have left the teaching board stranded on a level the player now
 * reaches two puzzles AFTER their first powered gate. The seam's own test
 * already derived the level from the config; only this map disagreed.
 *
 * Still deliberately narrow: one entry, one lookup, and nothing that could grow
 * into a level editor.
 */
export function scriptedLevelsFor(config) {
    return { [config.unlocks.poweredGatesUnlockLevel]: poweredGatesIntroBoard };
}
/**
 * WP-63.E2 — L25, the powered-gates introduction.
 *
 * Two blocks, one plate, one powered gate, on the band's own grid (6×6 for the
 * Medium gameplay band that L25 belongs to). Red (1×2, top-left) faces its
 * right-wall gate, which is SHUT. The plate sits AGAINST that same wall, in
 * red's colour, with its trace running up the wall line to the gate. Blue
 * (1×2, vertical, on the left edge) slides right until the wall stops it — and
 * the wall is where the plate is, so one drag parks it, the trace lights, the
 * shutter opens; red slides out; blue then drops out through its own gate on
 * the bottom wall — which it can only do AFTER red, because leaving the plate
 * shuts red's gate again. The hold, taught by the only order the board allows.
 *
 * The plate is on the wall for a reason beyond legibility: a move in this game
 * is a FULL slide (the solver's, the hint's and the ghost hand's move alike),
 * so a plate mid-lane would be a cell the block passes over, never one it
 * stops on. Impossible to misread by construction: red's one lane points
 * straight at the shut gate, nothing else on the board moves anything, and
 * the plate is the only mark on the floor.
 */
export function poweredGatesIntroBoard(config) {
    // Module 65 (WP-65.A2) — derived, not the literal 25. The board IS the powered
    // unlock level, and hardcoding it here meant a board served at level 22 still
    // announced itself as level 25 — so the HUD, the save and the analytics all
    // disagreed with the level the player was actually on.
    const levelNumber = config.unlocks.poweredGatesUnlockLevel;
    const difficulty = targetScoreForLevel(config, levelNumber).difficulty;
    const gridSize = bandConfigFor(config, difficulty).gridSize;
    const plateCol = gridSize - 1;
    const plates = [{ id: 'p-red', cell: { row: 3, col: plateCol }, gateColorId: 'Red' }];
    const exits = [
        {
            position: { row: 1, col: gridSize - 1 },
            direction: 'right',
            colorId: 'Red',
            span: 1,
            powered: { plateId: 'p-red' },
        },
        { position: { row: gridSize - 1, col: plateCol }, direction: 'down', colorId: 'Blue', span: 1 },
    ];
    const blocks = [
        {
            id: 'red',
            colorId: 'Red',
            size: 2,
            orientation: 'horizontal',
            type: 'target',
            position: { row: 1, col: 1 },
            cells: SHAPE_TEMPLATES['1x2'],
            movementConstraint: 'free',
        },
        {
            id: 'blue',
            colorId: 'Blue',
            size: 2,
            orientation: 'vertical',
            type: 'target',
            position: { row: 2, col: 0 },
            cells: [
                { r: 0, c: 0 },
                { r: 1, c: 0 },
            ],
            movementConstraint: 'free',
        },
    ];
    return {
        puzzle: { levelNumber, gridSize, exits, difficulty, plates },
        blocks,
        moveCount: 0,
        hintsUsed: 0,
        undoStack: [],
    };
}
/** The scripted board for this level as a full `FreeLevel`, or null when there is none. */
export function scriptedLevel(levelNumber, config) {
    const build = scriptedLevelsFor(config)[levelNumber];
    if (!build)
        return null;
    const state = build(config);
    const { exits, gridSize, plates } = state.puzzle;
    const opts = plates ? { plates } : {};
    const check = clearableFree(state.blocks, exits, gridSize, { maxNodes: 50000, ...opts });
    if (!check.clearable)
        throw new Error(`scripted level ${levelNumber} is not clearable`);
    const proxy = proxyScoreBoard(state.blocks, exits, gridSize, config.difficultyScoreWeights, plates);
    const target = targetScoreForLevel(config, levelNumber);
    return {
        state,
        metrics: {
            attempts: 1,
            clearingDepth: check.depth,
            // Module 83 — a scripted board is still a level the Reveal can show and
            // the budget floor must cover.
            solution: shortSolve(state.blocks, exits, gridSize, {
                maxNodes: config.moveBudget.solution.maxNodes,
                ...opts,
            }).moves,
            solutionSource: 'search',
            difficultyScore: null,
            difficultyMetrics: null,
            targetScore: target.target,
            proxyScore: proxy.score,
            proxyPar: proxy.metrics.moves,
            frameworkBand: target.band.name,
            isRecovery: target.isRecovery,
            isMilestone: target.isMilestone,
            nodes: check.nodes,
            blockCount: state.blocks.length,
            colourCount: new Set(state.blocks.map((b) => b.colorId)).size,
            shapeIds: state.blocks.map((b) => (b.orientation === 'vertical' ? '1x2' : '1x2')),
            arrowCount: 0,
            poweredCount: exits.filter((e) => e.powered).length,
            dependencies: dependencyCount(state.blocks, exits, gridSize, plates),
            layoutSignature: layoutSignature(state.blocks, exits, plates),
            usedFallback: false,
            rejects: {
                placement: 0,
                gates: 0,
                preExited: 0,
                tooLoose: 0,
                repeatedLayout: 0,
                missingArrows: 0,
                unsolvable: 0,
                tooEasy: 0,
                missingPowered: 0,
                unroutableTrace: 0,
                selfOpeningGate: 0,
            },
        },
    };
}
